/**
 * API client + hooks TanStack Query del Mundo A (FASE 1.2).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";

// ─── Tipos (espejan el backend src/types/estandares.ts) ───
export type TipoEstandar    = "CONOCER" | "ISO" | "Interno";
export type EstadoEstandar  = "borrador" | "revision" | "publicado" | "deprecado";
export type TipoEvaluacion  = "teorica" | "practica" | "mixta";
export type TipoCriterio    = "desempeno" | "producto" | "conocimiento" | "actitud" | "emergente" | "requisito_operativo";
export type ResultadoCriterio = "cumple_no_cumple" | "escala" | "binario_observado";
export type TipoInstrumento = "checklist" | "rubrica" | "examen" | "formulario" | "observacion";
export type TipoEvidencia   = "foto" | "video" | "audio" | "documento" | "firma" | "observacion";
export type ResponsableEvid = "candidato" | "instructor" | "evaluador" | "sistema";
export type SeveridadRegla  = "baja" | "media" | "alta" | "critica";
export type AccionRegla     = "warn" | "block_dictamen" | "block_cierre" | "require_observation";

export interface Estandar {
    id: string;
    // §1 Identificación
    codigo: string;
    nombre: string;
    tipo: TipoEstandar;
    version: string;
    vigencia_meses: number;
    nivel_competencia: number | null;
    // §2 Propósito
    proposito: string | null;
    descripcion: string | null;
    descripcion_general: string | null;
    justificacion: string | null;
    ambito_aplicacion: string | null;
    // §3 Clasificación SMO
    sector: string | null;
    subsector: string | null;
    area_competencia: string | null;
    ocupaciones_smo: Array<{ codigo?: string; nombre: string }>;
    // §5 Evaluación
    criterio_aprobacion: string | null;
    pct_global_minimo: number | null;
    pct_criticos_minimo: number | null;
    duracion_eval_horas: string | null;
    // §6 Recursos
    lugar_evaluacion: string | null;
    condiciones_seguridad: string | null;
    equipos_requeridos: Array<{ nombre: string; especificacion?: string }>;
    materiales_requeridos: Array<{ nombre: string; cantidad?: string }>;
    // Estado
    estado: EstadoEstandar;
    pdf_fuente_s3_key: string | null;
    config: Record<string, unknown>;
    metadata: Record<string, unknown>;
    publicado_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface EstandarListItem extends Estandar {
    elementos_count: number;
    criterios_count: number;
    candidatos_activos: number;
    certificados_emitidos: number;
}

export interface Elemento {
    id: string;
    estandar_id: string;
    codigo: string;
    nombre: string;
    descripcion_operativa: string | null;
    tipo_evaluacion: TipoEvaluacion;
    orden: number;
    estado: EstadoEstandar;
    created_at: string;
    updated_at: string;
}

export interface Criterio {
    id: string;
    elemento_id: string;
    codigo: string;
    tipo: TipoCriterio;
    descripcion: string;
    obligatorio: boolean;
    critico: boolean;
    observacion_obligatoria: boolean;
    resultado_permitido: ResultadoCriterio;
    orden: number;
    regla_ia: string | null;
    pista_candidato: string | null;
    pista_instructor: string | null;
}

export interface EvidenciaTipo {
    id: string;
    criterio_id: string;
    tipo: TipoEvidencia;
    formato: string | null;
    tamano_max_mb: number | null;
    obligatoria: boolean;
    bloquea_dictamen: boolean;
    responsable: ResponsableEvid;
    instrucciones: string | null;
    requiere_exif: boolean;
    requiere_geofence: boolean;
    orden: number;
}

export interface Instrumento {
    id: string;
    estandar_id: string;
    tipo: TipoInstrumento;
    nombre: string;
    version: string;
    descripcion: string | null;
    schema: Record<string, unknown>;
    activo: boolean;
    criterios_count: number;
}

export interface Regla {
    id: string;
    estandar_id: string | null;
    codigo: string;
    titulo: string;
    descripcion: string;
    severidad: SeveridadRegla;
    accion: AccionRegla;
    activa: boolean;
    orden: number;
}

export interface EstandarVersion {
    id: string;
    estandar_id: string;
    version: string;
    snapshot: Record<string, unknown>;
    snapshot_sha256: string;
    notas: string | null;
    publicado_at: string;
}

export interface EstandarArbol {
    estandar_id: string;
    estandar_codigo: string;
    estandar_nombre: string;
    version: string;
    tipo: TipoEstandar;
    estado: EstadoEstandar;
    elementos: Array<Elemento & {
        criterios: Array<Criterio & {
            evidencias_tipo: EvidenciaTipo[] | null;
        }> | null;
    }> | null;
}

// ═══════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════
export const QK = {
    estandares: (filters?: Record<string, unknown>) => ["estandares", "list", filters] as const,
    estandar:   (id: string) => ["estandares", "detail", id] as const,
    arbol:      (id: string) => ["estandares", "arbol", id] as const,
    versiones:  (id: string) => ["estandares", "versiones", id] as const,
    elementos:  (estandarId: string) => ["estandares", estandarId, "elementos"] as const,
    criterios:  (elementoId: string) => ["elementos", elementoId, "criterios"] as const,
    evidencias: (criterioId: string) => ["criterios", criterioId, "evidencias-tipo"] as const,
    instrumentos: (estandarId: string) => ["estandares", estandarId, "instrumentos"] as const,
    reglas:     (estandarId: string) => ["estandares", estandarId, "reglas"] as const,
};

// ═══════════════════════════════════════════════════════════════════
// ESTÁNDARES
// ═══════════════════════════════════════════════════════════════════
export function useEstandares(filters: {
    estado?: string; tipo?: string; sector?: string; q?: string;
} = {}) {
    const qs = new URLSearchParams();
    if (filters.estado) qs.set("estado", filters.estado);
    if (filters.tipo)   qs.set("tipo", filters.tipo);
    if (filters.sector) qs.set("sector", filters.sector);
    if (filters.q)      qs.set("q", filters.q);
    const path = `/estandares${qs.toString() ? `?${qs}` : ""}`;
    return useQuery({
        queryKey: QK.estandares(filters),
        queryFn: () => api.get<EstandarListItem[]>(path),
    });
}

export function useEstandar(id: string | undefined) {
    return useQuery({
        queryKey: QK.estandar(id ?? "_"),
        queryFn: () => api.get<Estandar>(`/estandares/${id}`),
        enabled: !!id,
    });
}

export function useEstandarArbol(id: string | undefined) {
    return useQuery({
        queryKey: QK.arbol(id ?? "_"),
        queryFn: () => api.get<EstandarArbol>(`/estandares/${id}/arbol`),
        enabled: !!id,
    });
}

export function useVersiones(estandarId: string | undefined) {
    return useQuery({
        queryKey: QK.versiones(estandarId ?? "_"),
        queryFn: () => api.get<EstandarVersion[]>(`/estandares/${estandarId}/versiones`),
        enabled: !!estandarId,
    });
}

export function useCreateEstandar() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: Partial<Estandar>) => api.post<Estandar>("/estandares", body),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["estandares", "list"] }),
    });
}

export function useUpdateEstandar(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: Partial<Estandar>) => api.patch<Estandar>(`/estandares/${id}`, body),
        onSuccess: (data) => {
            qc.setQueryData(QK.estandar(id), data);
            qc.invalidateQueries({ queryKey: ["estandares", "list"] });
            qc.invalidateQueries({ queryKey: QK.arbol(id) });
        },
    });
}

export function usePublicarEstandar(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: { version: string; notas?: string }) =>
            api.post<EstandarVersion>(`/estandares/${id}/publicar`, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK.estandar(id) });
            qc.invalidateQueries({ queryKey: QK.versiones(id) });
            qc.invalidateQueries({ queryKey: QK.arbol(id) });
        },
    });
}

// ═══════════════════════════════════════════════════════════════════
// ELEMENTOS
// ═══════════════════════════════════════════════════════════════════
export function useElementos(estandarId: string | undefined) {
    return useQuery({
        queryKey: QK.elementos(estandarId ?? "_"),
        queryFn: () => api.get<Elemento[]>(`/estandares/${estandarId}/elementos`),
        enabled: !!estandarId,
    });
}

export function useCreateElemento(estandarId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: Partial<Elemento>) =>
            api.post<Elemento>(`/estandares/${estandarId}/elementos`, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK.elementos(estandarId) });
            qc.invalidateQueries({ queryKey: QK.arbol(estandarId) });
        },
    });
}

export function useUpdateElemento(estandarId: string, elementoId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: Partial<Elemento>) =>
            api.patch<Elemento>(`/estandares/elementos/${elementoId}`, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK.elementos(estandarId) });
            qc.invalidateQueries({ queryKey: QK.arbol(estandarId) });
        },
    });
}

export function useDeleteElemento(estandarId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (elementoId: string) =>
            api.delete<{ ok: true }>(`/estandares/elementos/${elementoId}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK.elementos(estandarId) });
            qc.invalidateQueries({ queryKey: QK.arbol(estandarId) });
        },
    });
}

// ═══════════════════════════════════════════════════════════════════
// CRITERIOS
// ═══════════════════════════════════════════════════════════════════
export function useCriterios(elementoId: string | undefined) {
    return useQuery({
        queryKey: QK.criterios(elementoId ?? "_"),
        queryFn: () => api.get<Criterio[]>(`/estandares/elementos/${elementoId}/criterios`),
        enabled: !!elementoId,
    });
}

export function useCreateCriterio(elementoId: string, estandarId?: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: Partial<Criterio>) =>
            api.post<Criterio>(`/estandares/elementos/${elementoId}/criterios`, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK.criterios(elementoId) });
            if (estandarId) qc.invalidateQueries({ queryKey: QK.arbol(estandarId) });
        },
    });
}

export function useUpdateCriterio(elementoId: string, criterioId: string, estandarId?: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: Partial<Criterio>) =>
            api.patch<Criterio>(`/estandares/criterios/${criterioId}`, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK.criterios(elementoId) });
            if (estandarId) qc.invalidateQueries({ queryKey: QK.arbol(estandarId) });
        },
    });
}

export function useDeleteCriterio(elementoId: string, estandarId?: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (criterioId: string) =>
            api.delete<{ ok: true }>(`/estandares/criterios/${criterioId}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK.criterios(elementoId) });
            if (estandarId) qc.invalidateQueries({ queryKey: QK.arbol(estandarId) });
        },
    });
}

// ═══════════════════════════════════════════════════════════════════
// EVIDENCIAS_TIPO
// ═══════════════════════════════════════════════════════════════════
export function useEvidenciasTipo(criterioId: string | undefined) {
    return useQuery({
        queryKey: QK.evidencias(criterioId ?? "_"),
        queryFn: () => api.get<EvidenciaTipo[]>(`/estandares/criterios/${criterioId}/evidencias-tipo`),
        enabled: !!criterioId,
    });
}

export function useCreateEvidenciaTipo(criterioId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: Partial<EvidenciaTipo>) =>
            api.post<EvidenciaTipo>(`/estandares/criterios/${criterioId}/evidencias-tipo`, body),
        onSuccess: () => qc.invalidateQueries({ queryKey: QK.evidencias(criterioId) }),
    });
}

// ═══════════════════════════════════════════════════════════════════
// INSTRUMENTOS
// ═══════════════════════════════════════════════════════════════════
export function useInstrumentos(estandarId: string | undefined) {
    return useQuery({
        queryKey: QK.instrumentos(estandarId ?? "_"),
        queryFn: () => api.get<Instrumento[]>(`/estandares/${estandarId}/instrumentos`),
        enabled: !!estandarId,
    });
}

export function useCreateInstrumento(estandarId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: Partial<Instrumento>) =>
            api.post<Instrumento>(`/estandares/${estandarId}/instrumentos`, body),
        onSuccess: () => qc.invalidateQueries({ queryKey: QK.instrumentos(estandarId) }),
    });
}

// ═══════════════════════════════════════════════════════════════════
// REGLAS
// ═══════════════════════════════════════════════════════════════════
export function useReglas(estandarId: string | undefined) {
    return useQuery({
        queryKey: QK.reglas(estandarId ?? "_"),
        queryFn: () => api.get<Regla[]>(`/estandares/${estandarId}/reglas`),
        enabled: !!estandarId,
    });
}

export function useCreateRegla(estandarId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: Partial<Regla>) =>
            api.post<Regla>("/estandares/reglas", { ...body, estandar_id: estandarId }),
        onSuccess: () => qc.invalidateQueries({ queryKey: QK.reglas(estandarId) }),
    });
}
